import type { RoleContext } from '../../lib/roles';
import type { RoleScope } from './roleResolver';
import type { DataScopeV1 } from '../../types/effectiveScope';
import { SCOPE_VERSION } from '../../types/effectiveScope';

export type MergeSupervisorLofInput = {
  nextRoleContext: RoleContext;
  scope: RoleScope;
  hasSupervisorRole: boolean;
  hasLofRef: boolean;
  supervisorDeptIds: string[];
  lofId: string | null;
  lofIdsArray: string[];
};

/**
 * Pure merge: SN normal pages use LOF-resolved departments only (see docs/Role breakdown table.md).
 * Does not perform Firestore I/O.
 */
export function mergeSupervisorLofScope(input: MergeSupervisorLofInput): {
  finalRoleContext: RoleContext;
  finalScope: RoleScope;
  dataScope: DataScopeV1;
} {
  const {
    nextRoleContext,
    scope,
    hasSupervisorRole,
    hasLofRef,
    supervisorDeptIds,
    lofId,
    lofIdsArray,
  } = input;

  const shouldRestrictToLof = hasSupervisorRole;
  const useLofFallback = hasLofRef && supervisorDeptIds.length > 0;
  const explicitlyAssignedDeptIds = (scope.departmentIds ?? []).filter(
    (id): id is string => typeof id === 'string' && id !== '*',
  );

  const resolvedFallback = useLofFallback
    ? supervisorDeptIds
    : hasSupervisorRole && hasLofRef
      ? []
      : explicitlyAssignedDeptIds;

  const effectiveDeptIds = shouldRestrictToLof ? resolvedFallback : undefined;

  const finalRoleContext: RoleContext =
    effectiveDeptIds !== undefined
      ? {
          ...nextRoleContext,
          lineOfWorkDepartmentIds: effectiveDeptIds,
          ...(lofId ? { lofId } : {}),
          ...(lofIdsArray.length ? { lofIds: lofIdsArray } : {}),
        }
      : {
          ...nextRoleContext,
          ...(lofId ? { lofId } : {}),
          ...(lofIdsArray.length ? { lofIds: lofIdsArray } : {}),
        };

  const finalScope: RoleScope =
    effectiveDeptIds !== undefined
      ? {
          ...scope,
          departmentIds: effectiveDeptIds,
          view: { ...scope.view, departmentIds: effectiveDeptIds },
          edit: { ...scope.edit, departmentIds: effectiveDeptIds },
        }
      : scope;

  const dataScope: DataScopeV1 = {
    scopeVersion: SCOPE_VERSION,
    lineOfWorkDepartmentIds: effectiveDeptIds ?? nextRoleContext.lineOfWorkDepartmentIds ?? [],
    ...(lofId ? { lofId } : {}),
    ...(lofIdsArray.length ? { lofIds: lofIdsArray } : {}),
  };

  return { finalRoleContext, finalScope, dataScope };
}
